/**
 * Komal OS - CRM: Account detail.
 *
 * Contacts, deals, activity log and reminders for one account, plus
 * inline forms to add each. Server-side access is owner-or-CEO (see
 * app/routes/crm_routes.py's _get_account_or_403) - a 403 here means this
 * account belongs to someone else.
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Plus, Phone, Mail, Calendar, CheckCircle2, Clock, ArrowLeft, Pencil, Trash2,
} from 'lucide-react';
import {
  getAccount, updateAccount, deleteAccount, addContact, updateContact, deleteContact, createDeal, logActivity,
  createReminder, completeReminder, listEvents, listProducts, createProduct,
} from '../../services/crmService';
import useUIStore from '../../store/uiStore';
import useAuthStore from '../../store/authStore';

const STAGE_META = {
  new: 'bg-blue-50 text-blue-700 border-blue-200',
  contacted: 'bg-blue-50 text-blue-700 border-blue-200',
  qualified: 'bg-purple-50 text-purple-700 border-purple-200',
  quoted: 'bg-komal-blue/10 text-komal-blue border-komal-blue/30',
  won: 'bg-green-50 text-green-700 border-green-200',
  lost: 'bg-red-50 text-red-700 border-red-200',
};

const emptyContact = () => ({ name: '', title: '', phone: '', email: '' });
const emptyDeal = () => ({ title: '', value: '', currency: 'INR', expected_close_date: '', event_id: '', product_ids: [] });
const emptyActivity = () => ({ note_text: '', activity_type: 'note' });
const emptyReminder = () => ({ due_at: '', note: '' });

export default function AccountDetailPage() {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useUIStore();
  const { user } = useAuthStore();

  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [products, setProducts] = useState([]);

  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);

  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm, setContactForm] = useState(emptyContact());
  const [editingContactId, setEditingContactId] = useState(null);
  const [editContactForm, setEditContactForm] = useState(emptyContact());
  const [showDealForm, setShowDealForm] = useState(false);
  const [dealForm, setDealForm] = useState(emptyDeal());
  const [newProductName, setNewProductName] = useState('');
  const [activityForm, setActivityForm] = useState(emptyActivity());
  const [showReminderForm, setShowReminderForm] = useState(false);
  const [reminderForm, setReminderForm] = useState(emptyReminder());
  const [saving, setSaving] = useState(false);

  const load = () => getAccount(accountId).then((res) => setAccount(res.data.account)).finally(() => setLoading(false));

  useEffect(() => {
    load();
    listEvents().then((res) => setEvents(res.data.events)).catch(() => {});
    listProducts().then((res) => setProducts(res.data.products)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  const startEdit = () => {
    setEditForm({
      name: account.name, country: account.country || '', state: account.state || '', city: account.city || '',
      address: account.address || '', website: account.website || '', industry_notes: account.industry_notes || '',
    });
    setEditing(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateAccount(accountId, editForm);
      showToast('Customer updated', 'success');
      setEditing(false);
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not update customer', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    // CEO-only server-side too (see crm_routes.py's require_ceo) - a real,
    // permanent delete of the account and everything under it (contacts,
    // deals, activity, reminders), not an archive - confirm before doing
    // anything irreversible.
    if (!window.confirm(`Permanently delete "${account.name}" and all its contacts, deals, and activity? This cannot be undone.`)) return;
    try {
      const res = await deleteAccount(accountId);
      showToast(res.data.message || 'Customer deleted', 'success');
      navigate('/crm/accounts');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not delete customer', 'error');
    }
  };

  const submitContact = async (e) => {
    e.preventDefault();
    if (!contactForm.name.trim()) { showToast('Name is required', 'error'); return; }
    setSaving(true);
    try {
      await addContact(accountId, contactForm);
      showToast('Contact added', 'success');
      setContactForm(emptyContact());
      setShowContactForm(false);
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not add contact', 'error');
    } finally {
      setSaving(false);
    }
  };

  const startEditContact = (c) => {
    setEditingContactId(c.id);
    setEditContactForm({ name: c.name, title: c.title || '', phone: c.phone || '', email: c.email || '' });
  };

  const submitEditContact = async (e) => {
    e.preventDefault();
    if (!editContactForm.name.trim()) { showToast('Name is required', 'error'); return; }
    setSaving(true);
    try {
      await updateContact(accountId, editingContactId, editContactForm);
      showToast('Contact updated', 'success');
      setEditingContactId(null);
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not update contact', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      await deleteContact(accountId, contactId);
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not delete contact', 'error');
    }
  };

  const submitDeal = async (e) => {
    e.preventDefault();
    if (!dealForm.title.trim()) { showToast('Deal title is required', 'error'); return; }
    setSaving(true);
    try {
      await createDeal({
        account_id: Number(accountId),
        title: dealForm.title,
        value: dealForm.value ? Number(dealForm.value) : null,
        currency: dealForm.currency,
        expected_close_date: dealForm.expected_close_date || null,
        event_id: dealForm.event_id || null,
        product_ids: dealForm.product_ids,
      });
      showToast('Deal created', 'success');
      setDealForm(emptyDeal());
      setShowDealForm(false);
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not create deal', 'error');
    } finally {
      setSaving(false);
    }
  };

  const addNewProduct = async () => {
    if (!newProductName.trim()) return;
    try {
      const res = await createProduct({ name: newProductName.trim() });
      setProducts((prev) => (prev.some((p) => p.id === res.data.product.id) ? prev : [...prev, res.data.product]));
      setDealForm((f) => ({ ...f, product_ids: [...f.product_ids, res.data.product.id] }));
      setNewProductName('');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not add product', 'error');
    }
  };

  const toggleProduct = (id) => {
    setDealForm((f) => ({
      ...f,
      product_ids: f.product_ids.includes(id) ? f.product_ids.filter((x) => x !== id) : [...f.product_ids, id],
    }));
  };

  const submitActivity = async (e) => {
    e.preventDefault();
    if (!activityForm.note_text.trim()) return;
    setSaving(true);
    try {
      await logActivity({ account_id: Number(accountId), ...activityForm });
      setActivityForm(emptyActivity());
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not log activity', 'error');
    } finally {
      setSaving(false);
    }
  };

  const submitReminder = async (e) => {
    e.preventDefault();
    if (!reminderForm.due_at) { showToast('Due date/time is required', 'error'); return; }
    setSaving(true);
    try {
      await createReminder({ account_id: Number(accountId), ...reminderForm });
      showToast('Reminder set', 'success');
      setReminderForm(emptyReminder());
      setShowReminderForm(false);
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not set reminder', 'error');
    } finally {
      setSaving(false);
    }
  };

  const markReminderDone = async (id) => {
    try {
      await completeReminder(id);
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not update reminder', 'error');
    }
  };

  if (loading) return <div className="min-h-screen p-6 text-white/80 text-sm">Loading…</div>;
  if (!account) return <div className="min-h-screen p-6 text-white/80 text-sm">Customer not found.</div>;

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-5xl mx-auto">
        <Link to="/crm/accounts" className="flex items-center gap-1.5 text-xs text-white/70 hover:text-white mb-3">
          <ArrowLeft size={13} /> Customers
        </Link>

        <div className="flex items-start justify-between mb-6">
          {!editing ? (
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                {account.name}
                <button onClick={startEdit} className="text-white/60 hover:text-white"><Pencil size={14} /></button>
              </h1>
              <p className="text-sm text-white/70 mt-0.5">
                {[[account.city, account.state, account.country].filter(Boolean).join(', '), account.website].filter(Boolean).join(' · ') || 'No details yet'} · Owner: {account.owner?.name || '—'}
              </p>
              {account.industry_notes && <p className="text-sm text-white/60 mt-1 max-w-xl">{account.industry_notes}</p>}
            </div>
          ) : (
            <form onSubmit={saveEdit} className="glass-card p-4 grid gap-2 w-full max-w-lg">
              <input required value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Company name" />
              <input value={editForm.country} onChange={(e) => setEditForm({ ...editForm, country: e.target.value })} className="border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Country" />
              <div className="grid grid-cols-2 gap-2">
                <input value={editForm.state} onChange={(e) => setEditForm({ ...editForm, state: e.target.value })} className="border border-gray-300 rounded px-3 py-2 text-sm" placeholder="State / region" />
                <input value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} className="border border-gray-300 rounded px-3 py-2 text-sm" placeholder="City" />
              </div>
              <textarea value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} className="border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Address" rows={2} />
              <input value={editForm.website} onChange={(e) => setEditForm({ ...editForm, website: e.target.value })} className="border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Website" />
              <textarea value={editForm.industry_notes} onChange={(e) => setEditForm({ ...editForm, industry_notes: e.target.value })} className="border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Industry notes" rows={2} />
              <div className="flex gap-2">
                <button disabled={saving} className="text-sm font-semibold text-white bg-green-700 rounded px-3 py-1.5">Save</button>
                <button type="button" onClick={() => setEditing(false)} className="text-sm text-gray-500 px-2">Cancel</button>
                {user?.role === 'CEO' && (
                  <button type="button" onClick={handleDeleteAccount} className="ml-auto flex items-center gap-1 text-xs font-medium text-red-600 hover:underline">
                    <Trash2 size={13} /> Permanently delete customer
                  </button>
                )}
              </div>
            </form>
          )}
        </div>

        <div className="grid gap-5 md:grid-cols-[280px_1fr]">
          <div className="flex flex-col gap-4">
            {/* Contacts */}
            <div className="glass-card p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-bold uppercase tracking-wide text-gray-500">Contacts ({account.contacts.length})</span>
                <button onClick={() => setShowContactForm((v) => !v)} className="text-komal-blue"><Plus size={15} /></button>
              </div>
              {showContactForm && (
                <form onSubmit={submitContact} className="grid gap-1.5 mb-3 bg-gray-50 rounded-lg p-2.5">
                  <input required placeholder="Name" value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-xs" />
                  <input placeholder="Title" value={contactForm.title} onChange={(e) => setContactForm({ ...contactForm, title: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-xs" />
                  <input placeholder="Phone" value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-xs" />
                  <input placeholder="Email" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-xs" />
                  <button disabled={saving} className="text-xs font-semibold text-white bg-komal-blue rounded px-2 py-1.5">Add contact</button>
                </form>
              )}
              {account.contacts.length === 0 ? (
                <p className="text-xs text-gray-400">No contacts yet.</p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {account.contacts.map((c) => (
                    <li key={c.id} className="py-2 text-xs group">
                      {editingContactId === c.id ? (
                        <form onSubmit={submitEditContact} className="grid gap-1.5 bg-gray-50 rounded-lg p-2">
                          <input required placeholder="Name" value={editContactForm.name} onChange={(e) => setEditContactForm({ ...editContactForm, name: e.target.value })} className="border border-gray-300 rounded px-2 py-1 text-xs" />
                          <input placeholder="Title" value={editContactForm.title} onChange={(e) => setEditContactForm({ ...editContactForm, title: e.target.value })} className="border border-gray-300 rounded px-2 py-1 text-xs" />
                          <input placeholder="Phone" value={editContactForm.phone} onChange={(e) => setEditContactForm({ ...editContactForm, phone: e.target.value })} className="border border-gray-300 rounded px-2 py-1 text-xs" />
                          <input placeholder="Email" value={editContactForm.email} onChange={(e) => setEditContactForm({ ...editContactForm, email: e.target.value })} className="border border-gray-300 rounded px-2 py-1 text-xs" />
                          <div className="flex gap-2">
                            <button disabled={saving} className="text-xs font-semibold text-white bg-green-700 rounded px-2 py-1">Save</button>
                            <button type="button" onClick={() => setEditingContactId(null)} className="text-xs text-gray-500 px-1">Cancel</button>
                          </div>
                        </form>
                      ) : (
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="font-semibold text-gray-800">{c.name}</div>
                            {c.title && <div className="text-gray-500">{c.title}</div>}
                            <div className="flex gap-3 mt-1 text-gray-400">
                              {c.phone && <span className="flex items-center gap-1"><Phone size={10} />{c.phone}</span>}
                              {c.email && <span className="flex items-center gap-1"><Mail size={10} />{c.email}</span>}
                            </div>
                            {c.source === 'card_scan' && <span className="inline-block mt-1 text-[10px] font-semibold text-purple-600 bg-purple-50 border border-purple-200 rounded-full px-1.5 py-0.5">Card scan</span>}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <button onClick={() => startEditContact(c)} className="text-gray-400 hover:text-komal-blue" title="Edit"><Pencil size={12} /></button>
                            <button onClick={() => handleDeleteContact(c.id)} className="text-gray-300 hover:text-red-500" title="Delete"><Trash2 size={12} /></button>
                          </div>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Reminders */}
            <div className="glass-card p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-bold uppercase tracking-wide text-gray-500">Reminders</span>
                <button onClick={() => setShowReminderForm((v) => !v)} className="text-komal-blue"><Plus size={15} /></button>
              </div>
              {showReminderForm && (
                <form onSubmit={submitReminder} className="grid gap-1.5 mb-3 bg-gray-50 rounded-lg p-2.5">
                  <input required type="datetime-local" value={reminderForm.due_at} onChange={(e) => setReminderForm({ ...reminderForm, due_at: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-xs" />
                  <input placeholder="Note" value={reminderForm.note} onChange={(e) => setReminderForm({ ...reminderForm, note: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-xs" />
                  <button disabled={saving} className="text-xs font-semibold text-white bg-komal-blue rounded px-2 py-1.5">Set reminder</button>
                </form>
              )}
              {account.reminders.length === 0 ? (
                <p className="text-xs text-gray-400">No reminders.</p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {account.reminders.map((r) => (
                    <li key={r.id} className="py-2 text-xs flex items-start justify-between gap-2">
                      <div className={r.completed_at ? 'line-through text-gray-300' : ''}>
                        <div className="font-semibold text-gray-800 flex items-center gap-1"><Clock size={10} />{new Date(r.due_at).toLocaleString()}</div>
                        {r.note && <div className="text-gray-500">{r.note}</div>}
                      </div>
                      {!r.completed_at && (
                        <button onClick={() => markReminderDone(r.id)} title="Mark done" className="text-gray-300 hover:text-green-600 shrink-0"><CheckCircle2 size={14} /></button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-4">
            {/* Deals */}
            <div className="glass-card p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-bold uppercase tracking-wide text-gray-500">Deals ({account.deals.length})</span>
                <button onClick={() => setShowDealForm((v) => !v)} className="flex items-center gap-1 text-xs font-semibold text-komal-blue"><Plus size={14} /> New deal</button>
              </div>
              {showDealForm && (
                <form onSubmit={submitDeal} className="grid gap-2 mb-3 bg-gray-50 rounded-lg p-3 sm:grid-cols-2">
                  <input required placeholder="Deal title" value={dealForm.title} onChange={(e) => setDealForm({ ...dealForm, title: e.target.value })} className="sm:col-span-2 border border-gray-300 rounded px-2 py-1.5 text-xs" />
                  <input type="number" placeholder="Value" value={dealForm.value} onChange={(e) => setDealForm({ ...dealForm, value: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-xs" />
                  <select value={dealForm.currency} onChange={(e) => setDealForm({ ...dealForm, currency: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-xs bg-white">
                    {['INR', 'USD', 'EUR', 'AED', 'GBP'].map((c) => <option key={c}>{c}</option>)}
                  </select>
                  <input type="date" value={dealForm.expected_close_date} onChange={(e) => setDealForm({ ...dealForm, expected_close_date: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-xs" />
                  <select value={dealForm.event_id} onChange={(e) => setDealForm({ ...dealForm, event_id: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-xs bg-white">
                    <option value="">No event</option>
                    {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
                  </select>

                  <div className="sm:col-span-2">
                    <div className="text-[10px] font-semibold uppercase text-gray-500 mb-1">Products quoted</div>
                    <div className="flex flex-wrap gap-1.5 mb-1.5">
                      {products.map((p) => (
                        <button type="button" key={p.id} onClick={() => toggleProduct(p.id)}
                          className={`text-[11px] font-semibold px-2 py-1 rounded-full border ${dealForm.product_ids.includes(p.id) ? 'bg-komal-blue text-white border-komal-blue' : 'bg-white text-gray-600 border-gray-300'}`}>
                          {p.name}
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-1.5">
                      <input placeholder="Add a new product…" value={newProductName} onChange={(e) => setNewProductName(e.target.value)} className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs" />
                      <button type="button" onClick={addNewProduct} className="text-xs font-semibold text-komal-blue border border-gray-300 rounded px-2">Add</button>
                    </div>
                  </div>

                  <button disabled={saving} className="sm:col-span-2 text-xs font-semibold text-white bg-green-700 rounded px-2 py-1.5">Create deal</button>
                </form>
              )}
              {account.deals.length === 0 ? (
                <p className="text-xs text-gray-400">No deals yet.</p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {account.deals.map((d) => (
                    <li key={d.id} className="py-2.5 flex items-start justify-between gap-2">
                      <div>
                        <div className="text-sm font-semibold text-gray-800">{d.title}</div>
                        <div className="text-xs text-gray-500">
                          {d.value ? `${d.currency} ${Number(d.value).toLocaleString()}` : 'No value set'}
                          {d.expected_close_date && <span className="inline-flex items-center gap-1 ml-2"><Calendar size={10} />{d.expected_close_date}</span>}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {d.products.map((p) => <span key={p.id} className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200">{p.name}</span>)}
                        </div>
                      </div>
                      <span className={`text-[11px] font-semibold px-2 py-1 rounded-full border shrink-0 ${STAGE_META[d.stage]}`}>{d.stage}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Activity log */}
            <div className="glass-card p-4">
              <span className="text-[11px] font-bold uppercase tracking-wide text-gray-500">Activity</span>
              <form onSubmit={submitActivity} className="flex gap-2 mt-2 mb-3">
                <select value={activityForm.activity_type} onChange={(e) => setActivityForm({ ...activityForm, activity_type: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-xs bg-white">
                  {['call', 'email', 'meeting', 'note'].map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <input placeholder="Log a call, email, meeting or note…" value={activityForm.note_text} onChange={(e) => setActivityForm({ ...activityForm, note_text: e.target.value })} className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-xs" />
                <button disabled={saving} className="text-xs font-semibold text-white bg-komal-blue rounded px-3 py-1.5">Log</button>
              </form>
              {account.activity_log.length === 0 ? (
                <p className="text-xs text-gray-400">Nothing logged yet.</p>
              ) : (
                <ul className="space-y-2.5">
                  {account.activity_log.map((a) => (
                    <li key={a.id} className="text-xs border-l-2 border-gray-200 pl-3">
                      <div className="text-gray-800">{a.note_text}</div>
                      <div className="text-gray-400 mt-0.5">{a.author?.name || 'System'} · {a.activity_type} · {new Date(a.created_at).toLocaleString()}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
